'use client';

/**
 * ProcessorElement Component
 *
 * Renders the Charge processor whenever a payment method is selected.
 * The processor stays mounted until the payment method changes (key=retryKey
 * forces remount on retry). Processing is triggered by startSignal — the
 * parent increments it each time the user clicks Pay.
 * Mounted as a sibling to UI to prevent remounting on display mode changes.
 */

import React from 'react';
import { ChargePaymentComponent } from '../gateways/componentSelector';
import { usePurchaseCaptcha } from '../config/PurchaseFlowConfigProvider';
import type {
  PaymentMethod,
  PurchaseType,
  PaymentProcessingResult,
  PurchaseMetadata
} from '../types';

// ============================================================================
// Types
// ============================================================================

export interface ProcessorElementProps {
  /** Incremented each time the user clicks Pay; processor starts on change */
  startSignal: number;
  /** Key for forcing re-render on retry */
  retryKey: number;
  /** Selected payment method */
  selectedPaymentMethod: PaymentMethod | null;
  /** Fee-adjusted amount to charge (already accounts for fees and current price) */
  amount: number;
  /** Whether to include bonus amount */
  withBonus: boolean;
  /** Purchase type */
  purchaseType?: PurchaseType;
  /** Additional metadata */
  metadata?: PurchaseMetadata;
  /** Whether to recover from previous error */
  recover: boolean;
  /** Server-provided base URL for recoverable PayPal retries (errorOrigin + errorPathname) */
  recoverBaseUrl?: string;
  /** Artifact from user click: ApplePaySession, pre-opened popup Window, etc. */
  clickContext?: any;
  /** Exchange rate for local-currency processing */
  fxRate: number;
  /** ISO currency code for local-currency processing (e.g., 'MXN') */
  currency: string;
  /** Status change callback */
  onStatusChange: (status: string, message: string) => void;
  /** Success callback */
  onSuccess: (result: PaymentProcessingResult) => void;
  /** Error callback */
  onError: (result: PaymentProcessingResult) => void;
  /** Cancel callback */
  onCancel: () => void;
  /** Soft dismiss — navigates back without tracking failure */
  onDismiss: () => void;
  /** Called when a processor that prepares its own pay button (Google Pay) is ready */
  onReady?: () => void;
  /** Shared ref the processor fills with a synchronous "open sheet" trigger (Google Pay) */
  nativeTriggerRef?: React.RefObject<(() => void) | null>;
}

// ============================================================================
// Component
// ============================================================================

export const ProcessorElement: React.FC<ProcessorElementProps> = ({
  startSignal,
  retryKey,
  selectedPaymentMethod,
  amount,
  withBonus,
  purchaseType,
  metadata,
  recover,
  recoverBaseUrl,
  clickContext,
  fxRate,
  currency,
  onStatusChange,
  onSuccess,
  onError,
  onCancel,
  onDismiss,
  onReady,
  nativeTriggerRef
}) => {
  const withCaptcha = usePurchaseCaptcha();

  if (!selectedPaymentMethod?.gatewayName) {
    return null;
  }

  return (
    <ChargePaymentComponent
      key={ retryKey }
      startSignal={ startSignal }
      withCaptcha={ withCaptcha }
      gatewayName={ selectedPaymentMethod.gatewayName }
      amount={ amount }
      withBonus={ withBonus }
      paymentSourceId={ selectedPaymentMethod.id }
      purchaseType={ purchaseType }
      advisorId={ metadata?.advisorId }
      conversationType={ metadata?.conversationType }
      clickSource={ metadata?.clickSource }
      topupFlow={ metadata?.topupFlow }
      purchaseContext={ metadata?.context }
      viaSite={ metadata?.viaSite }
      extraParams={ selectedPaymentMethod.extraParams }
      context={ selectedPaymentMethod.context }
      riskModel={ selectedPaymentMethod.riskModel }
      riskModelConfig={ selectedPaymentMethod.riskModelConfig }
      recover={ recover }
      recoverBaseUrl={ recoverBaseUrl }
      clickContext={ clickContext }
      fxRate={ fxRate }
      currency={ currency }
      onStatusChange={ onStatusChange }
      onSuccess={ onSuccess }
      onError={ onError }
      onCancel={ onCancel }
      onDismiss={ onDismiss }
      onReady={ onReady }
      nativeTriggerRef={ nativeTriggerRef }
    />
  );
};

export default ProcessorElement;
